/**
 * Reusable page building blocks shared by the public pages.
 *
 * Illustrations here are code-generated SVG and CSS. Nothing is copied from the source site, and
 * everything is replaceable by a CMS-uploaded asset where the content model allows it.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Breadcrumbs, ButtonLink, Card, LinkCard, Skeleton } from '@/components/ui';
import { useReveal } from '@/hooks';
import { cn } from '@/lib/cn';
import type { ArticleCard, CaseStudyCard, IndustryCard, MediaRef, ProductCard, ServiceCard, SolutionCard } from '@/services/types';

/* ------------------------------------------------------------------ motion */

/** Fades content up the first time it scrolls into view. Disabled under `prefers-reduced-motion`. */
export function Reveal({ children, delay = 0, className }: { children: ReactNode; delay?: number; className?: string }) {
  const [ref, visible] = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={cn('transition-[opacity,transform] duration-reveal ease-emphasis', className)}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(14px)',
        transitionDelay: visible ? `${delay}ms` : '0ms',
      }}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ media */

/**
 * Responsive image using the WebP renditions the backend generates on upload, with a graceful
 * placeholder when a CMS field has no asset yet.
 */
export function Figure({
  media,
  alt,
  className,
  aspect = 'aspect-[16/10]',
  priority,
  placeholderLabel,
}: {
  media: MediaRef | null | undefined;
  alt?: string;
  className?: string;
  aspect?: string;
  priority?: boolean;
  placeholderLabel?: string;
}) {
  if (!media?.url) {
    return (
      <div
        className={cn(
          'hairline-grid flex items-center justify-center rounded-lg border border-line bg-raised text-center',
          aspect,
          className,
        )}
      >
        <span className="px-6 text-xs text-muted">{placeholderLabel ?? 'Image not yet uploaded'}</span>
      </div>
    );
  }

  const srcSet = Object.values(media.variants ?? {})
    .filter((variant) => variant?.url && variant.width)
    .map((variant) => `${variant.url} ${variant.width}w`)
    .join(', ');

  return (
    <div className={cn('overflow-hidden rounded-lg bg-sunken', aspect, className)}>
      <img
        src={media.url}
        {...(srcSet ? { srcSet, sizes: '(max-width: 768px) 100vw, 50vw' } : {})}
        alt={alt ?? media.alt_text}
        width={media.width ?? undefined}
        height={media.height ?? undefined}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        {...(priority ? { fetchPriority: 'high' as const } : {})}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ iconography */

const ICON_PATHS: Record<string, string> = {
  'map-pin': 'M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Zm0-8.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z',
  video: 'M3 7.5A2.5 2.5 0 0 1 5.5 5h7A2.5 2.5 0 0 1 15 7.5v9A2.5 2.5 0 0 1 12.5 19h-7A2.5 2.5 0 0 1 3 16.5v-9Zm14 2.2 4-2.4v9.4l-4-2.4V9.7Z',
  cpu: 'M8 8h8v8H8V8Zm-3 3h3m-3 4h3m10-4h3m-3 4h3M11 5V2m4 3V2m-4 20v-3m4 3v-3M5 5h14v14H5V5Z',
  route: 'M6 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm12-8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 0v3a4 4 0 0 1-4 4H9',
  database: 'M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3Zm0 0v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  refresh: 'M20 12a8 8 0 1 1-2.7-6M20 4v5h-5',
  shield: 'M12 3l8 3v6c0 5-3.4 8.2-8 9-4.6-.8-8-4-8-9V6l8-3Z',
  'shield-check': 'M12 3l8 3v6c0 5-3.4 8.2-8 9-4.6-.8-8-4-8-9V6l8-3Zm-3 9 2.2 2.2L15.5 10',
  landmark: 'M3 21h18M5 21V10m4 11V10m6 11V10m4 11V10M12 3 3 8h18l-9-5Z',
  server: 'M4 5h16v5H4V5Zm0 9h16v5H4v-5Zm3-6.5h.01M7 16.5h.01',
  code: 'm9 8-5 4 5 4m6-8 5 4-5 4',
  'git-merge': 'M7 5a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0 4v10m0-5a6 6 0 0 0 6-6m4 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z',
  compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm3.5-12.5-2.1 5-5 2.1 2.1-5 5-2.1Z',
  'life-buoy': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-5.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-3.5-9L5.6 5.6m9.9 9.9 2.9 2.9m0-12.8-2.9 2.9m-6.9 6.9-2.9 2.9',
  signal: 'M4 20V9m5 11V4m5 16v-8m5 8V7',
  building: 'M5 21V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v17M15 9h3a1 1 0 0 1 1 1v11M3 21h18M8 7h3M8 11h3M8 15h3',
  truck: 'M3 7h11v9H3V7Zm11 3h4l3 3v3h-7v-6ZM7 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm10 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  siren: 'M7 18a5 5 0 0 1 10 0v2H7v-2Zm5-11V4m6.4 4.6 1.4-1.4M5.6 8.6 4.2 7.2M3 15h1m16 0h1',
  brain: 'M9 4a3 3 0 0 0-3 3 3 3 0 0 0-1 5.8V15a3 3 0 0 0 4 2.8V20h3V4H9Zm6 0v16h3v-2.2A3 3 0 0 0 22 15v-2.2A3 3 0 0 0 21 7a3 3 0 0 0-3-3h-3Z',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-9-9h18M12 3c2.5 2.4 3.8 5.5 3.8 9S14.5 18.6 12 21c-2.5-2.4-3.8-5.5-3.8-9S9.5 5.4 12 3Z',
  layers: 'm12 3 9 5-9 5-9-5 9-5Zm9 9-9 5-9-5m18 4-9 5-9-5',
  chart: 'M4 20h16M7 16V9m5 7V5m5 11v-4',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-4.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0-3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  settings: 'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7.4-2.1.1-1.4-.1-1.4 1.7-1.3-1.4-2.4-2 .8-2.4-1.4-.3-2.1h-2.8l-.3 2.1-2.4 1.4-2-.8-1.4 2.4 1.7 1.3-.1 1.4.1 1.4-1.7 1.3 1.4 2.4 2-.8 2.4 1.4.3 2.1h2.8l.3-2.1 2.4-1.4 2 .8 1.4-2.4-1.7-1.3Z',
  handshake: 'm7 12 3-3 3 3 3-3 3 3-4.5 4.5a2 2 0 0 1-2.8 0L7 12Zm-3 0 3-3M20 12l-3-3',
  award: 'M12 15a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm-3 .8L8 22l4-2 4 2-1-6.2',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7 0a3 3 0 1 0 0-6M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1m3 0v-1a5 5 0 0 0-2.5-4.3',
  flag: 'M6 21V4m0 0h11l-2 4 2 4H6',
  check: 'm5 12.5 4.5 4.5L19 7.5',
  'check-circle': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-4-9.2 2.8 2.8L16 9',
  calendar: 'M5 6h14v14H5V6Zm3-3v3m8-3v3M5 10h14',
  headset: 'M4 14v-2a8 8 0 0 1 16 0v2m-16 0a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 2Zm16 0a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 2Zm-2 4v1a3 3 0 0 1-3 3h-2',
  workflow: 'M5 5h5v5H5V5Zm9 9h5v5h-5v-5ZM10 7.5h4a2 2 0 0 1 2 2V14',
  key: 'M15 9a3 3 0 1 0-2.1 2.9L4 21h3v-2h2v-2h2l1.9-1.9A3 3 0 0 0 15 9Z',
  wrench: 'M14.7 6.3a4 4 0 1 0 3 6.9L20 15.5V20h-4.5l-2.3-2.3a4 4 0 0 1-6.9-3l2.4 2.4 2.1-2.1-2.4-2.4a4 4 0 0 1 6.3-4.3Z',
  scan: 'M4 8V4h4m8 0h4v4m0 8v4h-4M8 20H4v-4m4-4h8v4H8v-4Z',
  bell: 'M6 16V11a6 6 0 1 1 12 0v5l2 2H4l2-2Zm4 3.5a2 2 0 0 0 4 0',
  archive: 'M4 6h16v4H4V6Zm2 4v9h12v-9M10 14h4',
  'heart-pulse': 'M12 20s-7-4.5-7-9.5A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 7 3.5c0 5-7 9.5-7 9.5Zm-4-9h2.5l1 2 2-4 1 2H17',
  download: 'M12 4v10m0 0-3.5-3.5M12 14l3.5-3.5M5 19h14',
  upload: 'M12 16V6m0 0L8.5 9.5M12 6l3.5 3.5M5 19h14',
  book: 'M5 5c0-1 1-2 3-2h9v16H8c-2 0-3 1-3 2V5Zm0 0v16',
  'file-text': 'M6 3h8l4 4v14H6V3Zm3 8h6m-6 4h6',
  pen: 'M4 20h4l11-11-4-4L4 16v4Zm10-14 4 4',
  newspaper: 'M4 5h13v14H4V5Zm13 4h3v8a2 2 0 0 1-2 2h-1V9ZM7 9h7M7 12h7M7 15h5',
  megaphone: 'M4 11v3l12 5V6L4 11Zm12-3 4-2v12l-4-2M8 15v4h3',
  grid: 'M4 4h6v6H4V4Zm10 0h6v6h-6V4ZM4 14h6v6H4v-6Zm10 0h6v6h-6v-6Z',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13h.01M11 12h1v5h1',
  lock: 'M6 11h12v9H6v-9Zm2 0V8a4 4 0 0 1 8 0v3',
  'git-branch': 'M7 5a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0 4v10m10-14a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0 4a6 6 0 0 1-6 6',
  activity: 'M3 12h4l3-8 4 16 3-8h4',
  shapes: 'M12 3l5 8H7l5-8Zm5 10a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM3 14h8v7H3v-7Z',
  map: 'm9 4 6 2 6-2v14l-6 2-6-2-6 2V6l6-2Zm0 0v14m6-12v14',
  rocket: 'M12 3c3.5 2 5.5 5.5 5.5 9.5L14 16h-4l-3.5-3.5C6.5 8.5 8.5 5 12 3Zm0 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM9 18c-1 1.5-1 3-1 3s1.5 0 3-1m4 0c1.5 1 3 1 3 1s0-1.5-1-3',
};

/** Line icon from the design-system set. Falls back to a neutral glyph for unknown names. */
export function Icon({ name, className }: { name?: string; className?: string }) {
  const path = (name && ICON_PATHS[name]) || ICON_PATHS.layers!;
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={cn('h-6 w-6', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={path} />
    </svg>
  );
}

export function IconTile({ name, className }: { name?: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary',
        className,
      )}
    >
      <Icon name={name} className="h-5 w-5" />
    </span>
  );
}

/* ------------------------------------------------------------------ page header */

export function PageHero({
  eyebrow,
  title,
  description,
  breadcrumbs,
  actions,
  aside,
  background = 'mesh',
  meta,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  breadcrumbs?: { label: string; path: string }[];
  actions?: ReactNode;
  aside?: ReactNode;
  background?: 'mesh' | 'light' | 'subtle';
  meta?: ReactNode;
}) {
  const inverse = background === 'mesh';
  const backgrounds = { mesh: 'band-mesh', light: 'bg-surface', subtle: 'bg-raised' } as const;

  return (
    <section className={cn('pb-section-sm pt-10 md:pb-section md:pt-14', backgrounds[background])}>
      <div className="shell">
        {breadcrumbs && breadcrumbs.length > 1 && (
          <div className="mb-8">
            <Breadcrumbs items={breadcrumbs} inverse={inverse} />
          </div>
        )}
        <div className={cn('grid items-center gap-12', aside && 'lg:grid-cols-[1.15fr_1fr]')}>
          <div className="max-w-3xl">
            {eyebrow && (
              <p className={cn('text-eyebrow uppercase', inverse ? 'text-accent' : 'text-primary')}>{eyebrow}</p>
            )}
            <h1 className={cn('mt-4 text-display-1', inverse && 'text-white')}>{title}</h1>
            {description && (
              <p className={cn('mt-6 max-w-2xl text-lead', inverse ? 'text-white/75' : 'text-muted')}>{description}</p>
            )}
            {meta && <div className="mt-6">{meta}</div>}
            {actions && <div className="mt-9 flex flex-wrap gap-3">{actions}</div>}
          </div>
          {aside && <div>{aside}</div>}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ cards */

export function SolutionTile({ solution, className }: { solution: SolutionCard; className?: string }) {
  return (
    <LinkCard to={solution.url} label={`Read about ${solution.name}`} className={className}>
      <Card interactive className="h-full">
        <IconTile name={solution.icon} />
        <h3 className="mt-5 text-lg font-semibold text-ink group-hover:text-primary">{solution.name}</h3>
        {solution.tagline && <p className="mt-1 text-sm font-medium text-accent">{solution.tagline}</p>}
        <p className="mt-3 text-sm leading-relaxed text-muted">{solution.summary}</p>
        {solution.industry_names.length > 0 && (
          <p className="mt-4 text-xs text-muted">{solution.industry_names.slice(0, 3).join(' · ')}</p>
        )}
        <p aria-hidden="true" className="mt-5 text-sm font-semibold text-primary">
          Explore solution →
        </p>
      </Card>
    </LinkCard>
  );
}

export function ProductTile({ product, className }: { product: ProductCard; className?: string }) {
  return (
    <LinkCard to={product.url} label={`Read about ${product.name}`} className={className}>
      <Card interactive className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-3">
          <IconTile name={product.icon} />
          {product.version && <Badge tone="neutral">v{product.version}</Badge>}
        </div>
        <h3 className="mt-5 text-lg font-semibold text-ink group-hover:text-primary">{product.name}</h3>
        {product.tagline && <p className="mt-1 text-sm text-muted">{product.tagline}</p>}
        <p className="mt-3 text-sm leading-relaxed text-muted">{product.summary}</p>
        {product.feature_titles.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {product.feature_titles.map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-sm text-ink-soft">
                <Icon name="check" className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                {feature}
              </li>
            ))}
          </ul>
        )}
        <p aria-hidden="true" className="mt-auto pt-5 text-sm font-semibold text-primary">
          View product →
        </p>
      </Card>
    </LinkCard>
  );
}

export function ServiceTile({ service, className }: { service: ServiceCard; className?: string }) {
  return (
    <LinkCard to={service.url} label={`Read about ${service.name}`} className={className}>
      <Card interactive className="h-full">
        <IconTile name={service.icon} />
        <h3 className="mt-5 text-lg font-semibold text-ink group-hover:text-primary">{service.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-muted">{service.summary}</p>
      </Card>
    </LinkCard>
  );
}

export function IndustryTile({ industry, className }: { industry: IndustryCard; className?: string }) {
  return (
    <LinkCard to={industry.url} label={`Read about our work in ${industry.name}`} className={className}>
      <div className="group h-full overflow-hidden rounded-lg border border-white/10 bg-white/[0.04] p-6 transition-colors duration-standard hover:border-white/25 hover:bg-white/[0.07]">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-white/10 text-white">
          <Icon name={industry.icon} className="h-5 w-5" />
        </span>
        <h3 className="mt-5 text-lg font-semibold text-white">{industry.name}</h3>
        <p className="mt-3 text-sm leading-relaxed text-white/65">{industry.summary}</p>
        <p aria-hidden="true" className="mt-5 text-sm font-semibold text-accent">
          Sector detail →
        </p>
      </div>
    </LinkCard>
  );
}

export function CaseStudyTile({ study, className }: { study: CaseStudyCard; className?: string }) {
  return (
    <LinkCard to={study.url} label={`Read the ${study.title} case study`} className={className}>
      <Card interactive className="flex h-full flex-col overflow-hidden p-0">
        <Figure media={study.hero_image} aspect="aspect-[16/9]" className="rounded-none" placeholderLabel="Case study image" />
        <div className="flex flex-1 flex-col p-6">
          <div className="flex flex-wrap items-center gap-2">
            {study.industry_name && <Badge tone="primary">{study.industry_name}</Badge>}
            {study.year && <span className="text-xs text-muted">{study.year}</span>}
          </div>
          <h3 className="mt-3 text-lg font-semibold text-ink group-hover:text-primary">{study.title}</h3>
          <p className="mt-1 text-sm font-medium text-muted">{study.customer_display}</p>
          <p className="mt-3 text-sm leading-relaxed text-muted">{study.summary}</p>
          {study.headline_metric && (
            <p className="mt-5 border-t border-line pt-4">
              <span className="font-display text-2xl font-bold text-accent">
                {study.headline_metric.value}
                {study.headline_metric.unit && <span className="text-base"> {study.headline_metric.unit}</span>}
              </span>
              <span className="ml-2 text-sm text-muted">{study.headline_metric.label}</span>
            </p>
          )}
        </div>
      </Card>
    </LinkCard>
  );
}

export function ArticleTile({ article, className }: { article: ArticleCard; className?: string }) {
  const published = article.published_at
    ? new Date(article.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <LinkCard to={article.url} label={`Read ${article.title}`} className={className}>
      <Card interactive className="flex h-full flex-col overflow-hidden p-0">
        <Figure media={article.featured_image} aspect="aspect-[16/9]" className="rounded-none" placeholderLabel="Article image" />
        <div className="flex flex-1 flex-col p-6">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <Badge tone="accent">{article.article_type_label}</Badge>
            {article.category_name && <span>{article.category_name}</span>}
            {published && <span>· {published}</span>}
          </div>
          <h3 className="mt-3 text-base font-semibold leading-snug text-ink group-hover:text-primary">{article.title}</h3>
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted">{article.summary}</p>
          <p className="mt-auto pt-4 text-xs text-muted">
            {article.author_display && <span>{article.author_display} · </span>}
            {article.read_minutes} min read
          </p>
        </div>
      </Card>
    </LinkCard>
  );
}

/* ------------------------------------------------------------------ content patterns */

export function FeatureGrid({ items, columns = 3 }: { items: { title?: string; description?: string; icon?: string }[]; columns?: 2 | 3 }) {
  if (items.length === 0) return null;
  return (
    <ul className={cn('grid gap-6', columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2')}>
      {items.map((item, index) => (
        <li key={item.title ?? index}>
          <Reveal delay={index * 45}>
            <Card className="h-full">
              <IconTile name={item.icon} />
              {item.title && <h3 className="mt-4 text-base font-semibold text-ink">{item.title}</h3>}
              {item.description && <p className="mt-2 text-sm leading-relaxed text-muted">{item.description}</p>}
            </Card>
          </Reveal>
        </li>
      ))}
    </ul>
  );
}

export function CheckList({ items, columns = 2 }: { items: { title?: string; description?: string }[]; columns?: 1 | 2 }) {
  if (items.length === 0) return null;
  return (
    <ul className={cn('grid gap-x-10 gap-y-5', columns === 2 && 'sm:grid-cols-2')}>
      {items.map((item, index) => (
        <li key={item.title ?? index} className="flex gap-3">
          <Icon name="check-circle" className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
          <div>
            {item.title && <p className="font-medium text-ink">{item.title}</p>}
            {item.description && <p className="mt-1 text-sm leading-relaxed text-muted">{item.description}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function StringList({ items, columns = 2 }: { items: string[]; columns?: 1 | 2 }) {
  if (items.length === 0) return null;
  return (
    <ul className={cn('grid gap-3', columns === 2 && 'sm:grid-cols-2')}>
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-[0.9375rem] text-ink-soft">
          <Icon name="check" className="mt-1 h-4 w-4 shrink-0 text-accent" />
          {item}
        </li>
      ))}
    </ul>
  );
}

/** Chips for a technology stack. Purely presentational — filtering lives on the index pages. */
export function TechStack({ technologies, label }: { technologies: { name: string; slug: string }[]; label?: string }) {
  if (technologies.length === 0) return null;
  return (
    <div>
      {label && <p className="text-eyebrow uppercase text-muted">{label}</p>}
      <ul className={cn('flex flex-wrap gap-2', label && 'mt-3')}>
        {technologies.map((technology) => (
          <li
            key={technology.slug}
            className="rounded-full border border-line bg-surface px-3 py-1.5 font-mono text-xs text-ink-soft"
          >
            {technology.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Sticky in-page navigation for long detail pages. */
export function SubNav({ sections }: { sections: { id: string; label: string }[] }) {
  if (sections.length < 2) return null;
  return (
    <nav
      aria-label="On this page"
      className="sticky top-[var(--header-height)] z-30 -mx-5 border-b border-line bg-surface/95 px-5 backdrop-blur-md sm:mx-0 sm:px-0"
    >
      <ul className="scrollbar-none flex gap-1 overflow-x-auto">
        {sections.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              className="inline-block whitespace-nowrap px-4 py-3.5 text-sm font-medium text-muted no-underline transition-colors hover:text-primary hover:no-underline"
            >
              {section.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function CtaBand({
  heading,
  body,
  primaryLabel,
  primaryUrl,
  secondaryLabel,
  secondaryUrl,
}: {
  heading: string;
  body?: string;
  primaryLabel: string;
  primaryUrl: string;
  secondaryLabel?: string;
  secondaryUrl?: string;
}) {
  return (
    <section className="band-mesh section">
      <div className="shell text-center">
        <h2 className="mx-auto max-w-3xl text-display-2 text-white">{heading}</h2>
        {body && <p className="mx-auto mt-5 max-w-2xl text-lead text-white/75">{body}</p>}
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <ButtonLink to={primaryUrl} variant="inverse" size="lg">
            {primaryLabel}
          </ButtonLink>
          {secondaryLabel && secondaryUrl && (
            <ButtonLink
              to={secondaryUrl}
              variant="secondary"
              size="lg"
              className="border-white/25 bg-transparent text-white hover:border-white hover:text-white"
            >
              {secondaryLabel}
            </ButtonLink>
          )}
        </div>
      </div>
    </section>
  );
}

export function RelatedGrid({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h2 className="text-display-3">{title}</h2>
      <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ loading & error */

export function CardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="rounded-lg border border-line p-6">
          <Skeleton className="h-11 w-11" />
          <Skeleton className="mt-5 h-5 w-2/3" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-4/5" />
        </div>
      ))}
      <span className="sr-only">Loading content</span>
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="shell py-20" aria-busy="true">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="mt-6 h-12 w-3/4" />
      <Skeleton className="mt-4 h-5 w-2/3" />
      <div className="mt-14">
        <CardGridSkeleton count={3} />
      </div>
      <span className="sr-only">Loading page</span>
    </div>
  );
}

export function LoadError({ onRetry, message }: { onRetry?: () => void; message?: string }) {
  return (
    <div className="shell py-24 text-center">
      <p className="font-display text-xl font-semibold text-ink">We could not load this content</p>
      <p className="mx-auto mt-3 max-w-md text-sm text-muted">
        {message ?? 'The request did not complete. This is usually temporary — please try again.'}
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-contrast hover:bg-primary-hover"
          >
            Try again
          </button>
        )}
        <Link
          to="/contact"
          className="rounded-md border border-line-strong px-5 py-2.5 text-sm font-semibold text-ink no-underline hover:border-primary hover:text-primary hover:no-underline"
        >
          Contact us
        </Link>
      </div>
    </div>
  );
}
